var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { dateFormatHelper } from 'utils/date-format-helper';
import { CatalogProcessStatus } from '../../models/catalogs/CatalogProcess';
/**
 * Status strings that indicate successful completion
 */
export var SUCCESS_STATUSES = ['SUCCEEDED', 'SUCCESS'];
/**
 * Formats a date string to a long format
 */
export function formatDate(dateString) {
    return dateString ? dateFormatHelper.formatDateLongEastern(new Date(dateString)) : undefined;
}
/**
 * Calculates and formats the elapsed time between two dates
 */
export function formatElapsed(start, end) {
    return start && end ? dateFormatHelper.formatElapsedTime(new Date(start), new Date(end)) : undefined;
}
/**
 * Extracts step function execution details from metrics
 * Includes proper null checking to prevent runtime errors
 */
export function extractStepFunctionDetails(metrics) {
    var _a, _b, _c, _d, _e, _f, _g;
    var lastCompletionStart = formatDate((_a = metrics.lastCompleted) === null || _a === void 0 ? void 0 : _a.startTime);
    var lastCompletionEnd = formatDate((_b = metrics.lastCompleted) === null || _b === void 0 ? void 0 : _b.endTime);
    var lastCompletedElapsed = formatElapsed((_c = metrics.lastCompleted) === null || _c === void 0 ? void 0 : _c.startTime, (_d = metrics.lastCompleted) === null || _d === void 0 ? void 0 : _d.endTime);
    var executionInProgress = ((_e = metrics.running) === null || _e === void 0 ? void 0 : _e.exists) || false;
    var executionStart = formatDate((_f = metrics.running) === null || _f === void 0 ? void 0 : _f.startTime);
    var executionElapsed = formatElapsed((_g = metrics.running) === null || _g === void 0 ? void 0 : _g.startTime, (new Date()).toISOString());
    return {
        lastCompletionStart: lastCompletionStart,
        lastCompletionEnd: lastCompletionEnd,
        lastCompletedElapsed: lastCompletedElapsed,
        executionInProgress: executionInProgress,
        executionStart: executionStart,
        executionElapsed: executionElapsed,
    };
}
/**
 * Extracts next execution details from the next execution object
 */
export function extractNextExecutionDetails(nextExecution) {
    return (nextExecution === null || nextExecution === void 0 ? void 0 : nextExecution.enabled) && nextExecution.scheduledTime ? formatDate(nextExecution.scheduledTime) : undefined;
}
/**
 * Computes the overall process status based on various factors
 */
export function computeProcessStatus(lastCompletedStatus, running, inflightProcesses, failedProcesses) {
    if (running || inflightProcesses > 0) {
        return CatalogProcessStatus.IN_PROGRESS;
    }
    if (SUCCESS_STATUSES.includes(lastCompletedStatus)) {
        if (failedProcesses === 0) {
            return CatalogProcessStatus.COMPLETED;
        }
        return CatalogProcessStatus.COMPLETED_WITH_ISSUE;
    }
    return CatalogProcessStatus.FAILED;
}
/**
 * Builds the price sync status from CPQ sync and price ingest data
 */
export function buildPriceSyncStatus(cpqSync, priceIngest) {
    var inflightProcesses = cpqSync.priceIngestEventsQueue.approximateNumberOfMessages
        + cpqSync.priceIngestEventsQueue.approximateNumberOfMessagesNotVisible
        + cpqSync.priceIngestMatrixEventsQueue.approximateNumberOfMessages
        + cpqSync.priceIngestMatrixEventsQueue.approximateNumberOfMessagesNotVisible
        + priceIngest.rawEventsV2Queue.approximateNumberOfMessages
        + priceIngest.rawEventsV2Queue.approximateNumberOfMessagesNotVisible;
    var failedProcesses = cpqSync.priceIngestEventsDlq.approximateNumberOfMessages
        + cpqSync.priceIngestEventsDlq.approximateNumberOfMessagesNotVisible
        + cpqSync.priceIngestMatrixEventsDlq.approximateNumberOfMessages
        + cpqSync.priceIngestMatrixEventsDlq.approximateNumberOfMessagesNotVisible
        + priceIngest.rawEventsV2Dlq.approximateNumberOfMessages
        + priceIngest.rawEventsV2Dlq.approximateNumberOfMessagesNotVisible;
    var overallStatus = computeProcessStatus(cpqSync.priceSyncProcessor.lastCompleted.status, cpqSync.priceSyncProcessor.running.exists, inflightProcesses, failedProcesses);
    var stepFunctionStatus = extractStepFunctionDetails(cpqSync.priceSyncProcessor);
    return __assign(__assign({ status: overallStatus }, stepFunctionStatus), { processesRemaining: inflightProcesses + failedProcesses, nextScheduledRun: extractNextExecutionDetails(cpqSync.nextExecution), fullPayload: { cpqSync: cpqSync, priceIngest: priceIngest } });
}
/**
 * Builds the product sync status from product ingest data
 */
export function buildProductSyncStatus(productIngest) {
    var inflightProcesses = productIngest.rawEventsQueue.approximateNumberOfMessages
        + productIngest.rawEventsQueue.approximateNumberOfMessagesNotVisible;
    var failedProcesses = productIngest.rawEventsDlq.approximateNumberOfMessages
        + productIngest.rawEventsDlq.approximateNumberOfMessagesNotVisible;
    var overallStatus = computeProcessStatus(productIngest.cspProductLoad.lastCompleted.status, productIngest.cspProductLoad.running.exists || false, inflightProcesses, failedProcesses);
    var stepFunctionStatus = extractStepFunctionDetails(productIngest.cspProductLoad);
    return __assign(__assign({ status: overallStatus }, stepFunctionStatus), { processesRemaining: inflightProcesses + failedProcesses, nextScheduledRun: extractNextExecutionDetails(productIngest.nextExecution), fullPayload: productIngest });
}
/**
 * Builds the Knoll sync status from Knoll load data
 */
export function buildKnollSyncStatus(knollLoad) {
    var _a, _b;
    var overallStatus = computeProcessStatus((_a = knollLoad.knollCatalogExtract) === null || _a === void 0 ? void 0 : _a.lastCompleted.status, ((_b = knollLoad.knollCatalogExtract) === null || _b === void 0 ? void 0 : _b.running.exists) || false, 0, 0);
    var stepFunctionStatus = extractStepFunctionDetails(knollLoad.knollCatalogExtract);
    return __assign(__assign({}, stepFunctionStatus), { status: overallStatus, nextScheduledRun: 'On Demand', fullPayload: knollLoad });
}
/**
 * Builds the configured hierarchy status
 */
export function buildConfiguredHierarchyStatus(hierarchy) {
    var _a, _b, _c, _d, _e;
    var overallStatus = computeProcessStatus((_a = hierarchy.lastCompleted) === null || _a === void 0 ? void 0 : _a.status, false, 0, 0);
    var lastCompletionStart = formatDate((_b = hierarchy.lastCompleted) === null || _b === void 0 ? void 0 : _b.startTime);
    var lastCompletionEnd = formatDate((_c = hierarchy.lastCompleted) === null || _c === void 0 ? void 0 : _c.endTime);
    var lastCompletedElapsed = formatElapsed((_d = hierarchy.lastCompleted) === null || _d === void 0 ? void 0 : _d.startTime, (_e = hierarchy.lastCompleted) === null || _e === void 0 ? void 0 : _e.endTime);
    return {
        status: overallStatus,
        lastCompletionStart: lastCompletionStart,
        lastCompletionEnd: lastCompletionEnd,
        lastCompletedElapsed: lastCompletedElapsed,
        executionInProgress: false,
        nextScheduledRun: extractNextExecutionDetails(hierarchy.nextExecution),
        fullPayload: hierarchy,
    };
}
/**
 * Builds the data lake product status
 */
export function buildDataLakeProductStatus(dataLakeProduct) {
    var overallStatus = computeProcessStatus(dataLakeProduct.productSync.lastCompleted.status, dataLakeProduct.productSync.running.exists || false, 0, 0);
    var stepFunctionStatus = extractStepFunctionDetails(dataLakeProduct.productSync);
    return __assign(__assign({}, stepFunctionStatus), { status: overallStatus, nextScheduledRun: extractNextExecutionDetails(dataLakeProduct.nextExecution), fullPayload: dataLakeProduct });
}
/**
 * Builds the data lake price status
 */
export function buildDataLakePriceStatus(dataLakePrice) {
    var _a, _b;
    var overallStatus = computeProcessStatus((_a = dataLakePrice.priceSync) === null || _a === void 0 ? void 0 : _a.lastCompleted.status, ((_b = dataLakePrice.priceSync) === null || _b === void 0 ? void 0 : _b.running.exists) || false, 0, 0);
    var stepFunctionStatus = extractStepFunctionDetails(dataLakePrice.priceSync);
    return __assign(__assign({}, stepFunctionStatus), { status: overallStatus, nextScheduledRun: extractNextExecutionDetails(dataLakePrice.nextExecution), fullPayload: dataLakePrice });
}
export function determineOverallStatus(statuses) {
    if (statuses.includes(CatalogProcessStatus.UNKNOWN)) {
        return {
            status: CatalogProcessStatus.UNKNOWN,
            message: 'Error determining sync status',
        };
    }
    if (statuses.includes(CatalogProcessStatus.IN_PROGRESS)) {
        return {
            status: CatalogProcessStatus.IN_PROGRESS,
            message: 'Syncs are in progress',
        };
    }
    if (statuses.includes(CatalogProcessStatus.FAILED)) {
        return {
            status: CatalogProcessStatus.FAILED,
            message: 'Sync failure',
        };
    }
    if (statuses.includes(CatalogProcessStatus.COMPLETED_WITH_ISSUE)) {
        return {
            status: CatalogProcessStatus.COMPLETED_WITH_ISSUE,
            message: 'Syncs complete with issues',
        };
    }
    return {
        status: CatalogProcessStatus.COMPLETED,
        message: 'Syncs complete with no issues',
    };
}
